import { FurnitureManager } from './furnitureManager';
import { i18n } from '@/plugins/i18n';
import Element, { SpecyficType } from './element';
import store from '@/store';
import { shaderTranslator } from './shaders/util/shaderTranslator';

export class ProductDetailsManager {

    private furnitureManager = new FurnitureManager();
    private productData: any;

    constructor(productData: any) {
        this.productData = productData;
    }

    public get summaryDimensionText(): string {
        return i18n.tc('dimension');
    }

    public get summaryDrawersText(): string {
        return i18n.tc('summaryDrawers');
    }

    public get summaryHangersText(): string {
        return i18n.tc('summaryHangers');
    }

    public get summaryBodyText(): string {
        return i18n.tc('summaryBodyColor');
    }

    public get summaryShelfText(): string {
        return i18n.tc('summaryShelfColor');
    }

    public get summaryBarText(): string {
        return i18n.tc('summaryBarColor');
    }

    public get summaryBedHeadText(): string {
        return i18n.tc('summaryBedHeadColor');
    }

    public get summaryFrontsText(): string {
        const code: string = this.productData.productPreset.code;
        return (code === 't' || code === 'b')
            ? i18n.tc('summaryFrontsColor2')
            : i18n.tc('summaryFrontsColor');
    }

    public get summaryHandlesText(): string {
        return i18n.tc('summaryHandles');
    }

    public get summaryFrameText(): string {
        return i18n.tc('summaryFrame');
    }

    public get summaryLegsText(): string {
        return i18n.tc('summaryLegs');
    }

    public crete(): any {
        return [
            { name: this.summaryDimensionText, value: this.dimension },
            { name: this.summaryDrawersText, value: this.hasDrawers },
            { name: this.summaryHangersText, value: this.hasHangers },
            { name: this.summaryBodyText, value: this.bodyColor },
            { name: this.summaryShelfText, value: this.shelfColor },
            { name: this.summaryBarText, value: this.barColor },
            { name: this.summaryFrontsText, value: this.frontsColor },
            { name: this.summaryLegsText, value: this.legs },
            { name: this.summaryHandlesText, value: this.handles },
            { name: this.summaryBedHeadText, value: this.bedHeadColor },
            { name: this.summaryFrameText, value: this.frame },
        ].filter(
            (detail) => detail.value !== null,
        );
    }

    public get dimension(): string {
        if (!this.productData) {
            return '';
        } else {
            const w = i18n.tc('widthAbbreviation').toString();
            const h = i18n.tc('heightAbbreviation').toString();
            const d = i18n.tc('depthAbbreviation').toString();
            const dim = this.productData.productPreset.dimension_full;
            return `${w}${dim.width}/${d}${dim.depth}/${h}${dim.height} cm`;
        }
    }

    public get hasDrawers(): string {
        if (!this.canDisplay('hasDrawers')) {
            return null;
        } else {
            if (!this.productData) {
                return i18n.tc('no').toString();
            } else {
                return this.productData.elements.filter(
                    (x) => x.specyficType === SpecyficType.DRAWER,
                ).length
                    ? i18n.tc('yes').toString()
                    : i18n.tc('no').toString();
            }
        }
    }

    public get hasHangers(): string {
        if (!this.canDisplay('hasHangers')) {
            return null;
        } else {
            if (!this.productData) {
                return i18n.tc('no').toString();
            } else {
                return this.productData.elements.filter(
                    (x) => x.specyficType === SpecyficType.HANGER,
                ).length
                    ? i18n.tc('yes').toString()
                    : i18n.tc('no').toString();
            }
        }
    }

    public get bodyColor(): string {
        return this.getElementName(this.productData.shaders.selectedBodyName);
    }

    public get shelfColor(): string {
        if (!this.canDisplay('shelfColor')) {
            return null;
        } else {
            return this.toColor(this.furnitureManager.shelfs, [
                this.productData.shaders.selectedShelfName,
            ]);
        }
    }

    public get barColor(): string {
        if (!this.canDisplay('barColor')) {
            return null;
        } else {
            return this.toColor(this.furnitureManager.walls, [
                this.productData.shaders.selectedWallName,
            ]);
        }
    }

    public get bedHeadColor(): string {
        if (!this.canDisplay('bedHead')) {
            return null;
        } else {
            return this.toColor(this.furnitureManager.bedHead, [
                this.productData.shaders.selectedWallName,
            ]);
        }
    }

    public get frame(): string {
        if (!this.canDisplay('frame')) {
            return null;
        } else {
            return this.toFrame(store.getters.shaders.selectedFrame.key);
        }
    }

    public get frontsColor(): string {
        if (!this.canDisplay('frontsColor')) {
            return null;
        } else {
            const fronts = this.furnitureManager.fronts;
            return this.toColor(
                fronts,
                fronts
                    .map((x) => x.shaderName)
                    .filter((value, index, self) => self.indexOf(value) === index),
            );
        }
    }

    public get legs(): string {
        if (!this.canDisplay('legs')) {
            return null;
        } else {
            return this.toColor(this.furnitureManager.legs, [
                this.productData.shaders.selectedLegsName,
            ]);
        }
    }

    public get handles(): string {
        if (!this.canDisplay('handles')) {
            return null;
        } else {
            return this.toColor(this.furnitureManager.fronts, [
                this.productData.shaders.selectedHandleName,
            ]);
        }
    }

    public toColor(elements: Element[], shaderNames: string[]) {
        return elements.length
            ? shaderNames.map((x) => this.getElementName(x)).join(', ')
            : i18n.tc('lack').toString();
    }

    public toFrame(shaderName: string): string {
        switch (shaderName) {
            case 'ShaderLiftedFrame':
                return i18n.tc('liftedFrame');
            case 'ShaderFloorFrame':
                return i18n.tc('floorFrame');
            case 'ShaderFloorWithFlexibleSlatsFrame':
                return i18n.tc('floorWithFlexibleSlats');
        }
    }

    public getElementName(shaderName: string): string {
        return shaderTranslator.toLocaleText(shaderName);
    }

    public canDisplay(type: string): boolean {
        const code: string = this.productData.productPreset.code;

        switch (type) {
            case 'legs':
                return (
                    [
                        'p2x1',
                        'p1x1',
                        'r1x2',
                        'r1x1a',
                        's1x1',
                        's2x1',
                        's3x1',
                        's4x1',
                    ].indexOf(code) === -1
                );
            case 'handles':
                return (
                    [
                        's',
                        'ska',
                        'sk',
                        'l_90',
                        'l_120',
                        'l_140',
                        'l_160',
                        'l_180',
                        'skk',
                    ].indexOf(code) === -1
                );
            case 'frontsColor':
                return (
                    [
                        's',
                        'ska',
                        'sk',
                        'l_90',
                        'l_120',
                        'l_140',
                        'l_160',
                        'l_180',
                        'skk',
                    ].indexOf(code) === -1
                );
            case 'barColor':
                return (
                    [
                        's',
                        'ska',
                        'sk',
                        'l_90',
                        'l_120',
                        'l_140',
                        'l_160',
                        'l_180',
                        'skk',
                        'p1x1',
                        'r1x1',
                        'r1x1a',
                        's1x1',
                        'k1x1',
                    ].indexOf(code) === -1
                );
            case 'shelfColor':
                return (
                    [
                        's',
                        'ska',
                        'sk',
                        'l_90',
                        'l_120',
                        'l_140',
                        'l_160',
                        'l_180',
                        'skk',
                        't',
                        'b',
                    ].indexOf(code) === -1
                );
            case 'hasHangers':
                return (
                    [
                        's1x4',
                        's2x4',
                        's3x4',
                        's4x4',
                        's1x3',
                        's2x3',
                        's2x3',
                        's3x3',
                    ].indexOf(code) > -1
                );
            case 'hasDrawers':
                return (
                    [
                        's1x4',
                        's2x4',
                        's3x4',
                        's4x4',
                        's1x3',
                        's2x3',
                        's2x3',
                        's3x3',
                        'k2x1',
                        'k1x1',
                        'k3x1',
                        'k4x1',
                        'k1x2',
                        'k2x2',
                        'k3x2',
                        'k2x3',
                        'k3x3',
                        'k2x3',
                        'k4x2',
                        'b',
                        't',
                    ].indexOf(code) > -1
                );
            case 'bedHead':
            case 'frame':
                return ['l_90', 'l_120', 'l_140', 'l_160', 'l_180'].indexOf(code) > -1;
        }

        return true;
    }
}
